// kontaktInVorgangAbgleich.ts — F2 (PO 01.10.2026): Wird ein Anruf einem
// (weiteren) Vorgang zugeordnet und kennt der Vorgang einen Kontakt des
// Anrufs nicht, fragt KMVS: „<Kontakt> ist nicht im Vorgang <Titel> — mit
// aufnehmen?" Der Kunde eines weiteren Vorgangs wird NICHT automatisch an den
// Anruf gehängt; umgekehrt kommt ein Kontakt nur auf „Ja" in den Vorgang.
//
// Die Liste liefert der Server in der Antwort auf `addZuordnungen`
// (`fehlendeKontakte`: nur sichtbare Kontakte, nur Vorgänge mit Schreibrecht).
// Hier stehen die Texte, die Auswahl und ein kleiner Bote, damit JEDER Weg
// (Dialog, Nach-Anruf-Karte, „Neuer Vorgang", Auto-Rückruf, V4) dieselbe
// Rückfrage auslöst, ohne selbst ein Popup zu tragen.

import type { CallFehlendeKontakte } from "../../services/telephonyService";

export type KontaktAbgleichEintrag = CallFehlendeKontakte;

/** Nur Vorgänge, denen wirklich ein Kontakt fehlt. */
export function abgleichFragen(fehlende: CallFehlendeKontakte[] | null | undefined): KontaktAbgleichEintrag[] {
    return (fehlende ?? []).filter((e) => Array.isArray(e.kontakte) && e.kontakte.length > 0);
}

const name = (k: { id: number; displayName: string | null }) => k.displayName?.trim() || `Kontakt #${k.id}`;

/** „A", „A und B", „A, B und C". */
export function namensListe(kontakte: Array<{ id: number; displayName: string | null }>): string {
    const n = kontakte.map(name);
    if (n.length <= 1) return n[0] ?? "";
    return `${n.slice(0, -1).join(", ")} und ${n[n.length - 1]}`;
}

const titel = (e: KontaktAbgleichEintrag) => (e.vorgangTitel?.trim() ? `„${e.vorgangTitel.trim()}“` : `#${e.vorgangId}`);

/** Frage für EINEN Vorgang. */
export function frageEinVorgang(e: KontaktAbgleichEintrag): string {
    const verb = e.kontakte.length === 1 ? "ist" : "sind";
    return `${namensListe(e.kontakte)} ${verb} nicht im Vorgang ${titel(e)} — mit aufnehmen?`;
}

/** Zeile je Vorgang in der gesammelten Rückfrage. */
export function zeileMehrereVorgaenge(e: KontaktAbgleichEintrag): string {
    return `${namensListe(e.kontakte)} in ${titel(e)}`;
}

/** Was auf „Ja"/„Aufnehmen" geschrieben wird — nur angehakte Vorgänge. */
export function aufnahmeAuftraege(
    eintraege: KontaktAbgleichEintrag[],
    angehakt: ReadonlySet<number>,
): Array<{ vorgangId: number; contactIds: number[] }> {
    return eintraege
        .filter((e) => angehakt.has(e.vorgangId))
        .map((e) => ({ vorgangId: e.vorgangId, contactIds: e.kontakte.map((k) => k.id) }));
}

// ── Bote: Wege ohne eigenes Popup melden die Rückfrage hier an ────────────

type Hoerer = (eintraege: KontaktAbgleichEintrag[]) => void;
const hoerer = new Set<Hoerer>();

/**
 * Rückfrage anbieten, wenn es etwas zu fragen gibt. Liefert, ob jemand
 * zuhört (die Hülle steckt in der Nach-Anruf-Karte, die app-weit gemountet ist).
 */
export function kontaktAbgleichAnbieten(fehlende: CallFehlendeKontakte[] | null | undefined): boolean {
    const eintraege = abgleichFragen(fehlende);
    if (eintraege.length === 0 || hoerer.size === 0) return false;
    for (const h of hoerer) h(eintraege);
    return true;
}

export function aufKontaktAbgleichHoeren(h: Hoerer): () => void {
    hoerer.add(h);
    return () => {
        hoerer.delete(h);
    };
}
